import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AuthShell from "@/components/AuthShell";
import { useAuth, formatApiError } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import AddressInput from "@/components/AddressInput";
import LegalDocDialog, { LEGAL_DOCS } from "@/components/LegalDocDialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { User, Users, Building2, Trophy } from "lucide-react";
import { useCountry } from "@/lib/CountryContext";

const REGIONS = [
    { value: "HK", label: "Hong Kong SAR" },
    { value: "MO", label: "Macao SAR" },
    { value: "SG", label: "Singapore" },
    { value: "TW", label: "Taiwan" },
    { value: "CN", label: "Mainland China" },
    { value: "OTHER", label: "Other" },
];

const GENDERS = [
    { value: "female", label: "Female" },
    { value: "male", label: "Male" },
    { value: "nonbinary", label: "Non-binary" },
    { value: "prefer_not_to_say", label: "Prefer not to say" },
];

// Membership tiles carry a stable key and pull their display strings from
// the i18n resource so both card label + blurb translate on language change.
const MEMBERSHIP_TYPES = [
    { value: "individual", icon: User },
    { value: "family",     icon: Users },
    { value: "business",   icon: Building2 },
];

export default function Signup() {
    const { t } = useTranslation();
    const { refresh } = useAuth();
    // Signup country — persisted onto the user record and used forever
    // after as the source of truth for their checkout currency. Auto-set
    // from geo-detect / user's picker via CountryContext.
    const { country } = useCountry();
    const navigate = useNavigate();

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");

    const [gender, setGender] = useState("");
    const [phone, setPhone] = useState("");
    const [region, setRegion] = useState("HK");
    const [dob, setDob] = useState("");
    const [addr, setAddr] = useState({ address_line: "" });

    const [membershipType, setMembershipType] = useState("individual");
    const [companyName, setCompanyName] = useState("");

    const [promoCode, setPromoCode] = useState("");
    const [promoStatus, setPromoStatus] = useState({ state: "idle" });

    // ---- Referral prefill --------------------------------------------------
    const [searchParams] = useSearchParams();
    const [referralCode, setReferralCode] = useState(
        (searchParams.get("ref") || "").trim().toUpperCase()
    );
    const [referrerName, setReferrerName] = useState("");

    const [catalogue, setCatalogue] = useState([]);
    const [consents, setConsents] = useState({});
    // id of the consent whose document is open, or null
    const [openDoc, setOpenDoc] = useState(null);

    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/public/consents").then((r) => {
            setCatalogue(r.data);
            setConsents(
                r.data.reduce((acc, c) => {
                    acc[c.id] = false;
                    return acc;
                }, {})
            );
        });
    }, []);

    // Validate the referral code once (silent — invalid code just clears
    // the greeting; we don't gate signup on it).
    useEffect(() => {
        if (!referralCode) {
            setReferrerName("");
            return;
        }
        let cancelled = false;
        api.get(`/public/referrals/${encodeURIComponent(referralCode)}`)
            .then((r) => {
                if (cancelled) return;
                if (r.data?.valid) setReferrerName(r.data.referrer_name || "");
                else setReferrerName("");
            })
            .catch(() => !cancelled && setReferrerName(""));
        return () => {
            cancelled = true;
        };
    }, [referralCode]);

    const missingRequired = catalogue.some((c) => c.required && !consents[c.id]);

    const applyPromo = async () => {
        const code = promoCode.trim();
        if (!code) {
            setPromoStatus({ state: "idle" });
            return;
        }
        setPromoStatus({ state: "checking" });
        try {
            const { data } = await api.post("/promo-codes/validate", {
                code,
                context: "signup",
            });
            setPromoStatus({ state: "valid", promo: data });
        } catch (e2) {
            setPromoStatus({
                state: "invalid",
                message: formatApiError(e2) || "This promo code is not valid.",
            });
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (missingRequired) {
            setErr(t("signup.err_consents"));
            return;
        }
        if (membershipType === "business" && !companyName.trim()) {
            setErr(t("signup.err_company"));
            return;
        }
        if (membershipType !== "business" && !dob) {
            setErr(t("signup.err_dob"));
            return;
        }
        if (!addr.address_line?.trim()) {
            setErr(t("signup.err_address"));
            return;
        }
        setBusy(true);
        setErr("");
        try {
            const payload = {
                name,
                email,
                password,
                consents,
                membership_type: membershipType,
                company_name: membershipType === "business" ? companyName.trim() : null,
                profile: {
                    full_name: name,
                    gender: gender || null,
                    phone: phone || null,
                    region,
                    date_of_birth: dob || null,
                    address_line: addr.address_line?.trim() || null,
                    address_place_id: addr.address_place_id || null,
                    address_lat: addr.address_lat || null,
                    address_lng: addr.address_lng || null,
                    address_components: addr.address_components || null,
                },
            };
            if (promoCode.trim()) payload.promo_code = promoCode.trim();
            if (referralCode.trim()) payload.referral_code = referralCode.trim();
            if (country) payload.country = country;

            const { data } = await api.post("/auth/signup", payload);
            localStorage.setItem("perx_token", data.token);
            await refresh();
            toast.success(`Welcome to Perx, ${data.user.name}`);
            if (membershipType === "business") {
                // Seat-based memberships subscribe from the portal once
                // employees are added, so they skip the offer dialog.
                navigate("/member/employees", { replace: true });
            } else {
                try {
                    sessionStorage.setItem("perx_signup_offer", "1");
                } catch (_e) {
                    // storage disabled — the offer just won't show
                }
                navigate("/member", { replace: true });
            }
        } catch (e2) {
            setErr(formatApiError(e2));
        } finally {
            setBusy(false);
        }
    };

    return (
        <AuthShell
            title={t("signup.title")}
            subtitle={t("signup.subtitle")}
            side="checklist"
        >
            <form
                onSubmit={submit}
                className="space-y-8"
                data-testid="signup-form"
                noValidate
            >
                {referrerName && (
                    <div
                        data-testid="signup-referral-banner"
                        className="flex items-center gap-3 rounded-2xl border border-gold-300 bg-gold-50 px-4 py-3"
                    >
                        <Trophy size={18} className="text-gold-600 flex-shrink-0" />
                        <div className="text-sm text-navy-700">
                            {t("signup.referral_banner_1")}{" "}
                            <span className="font-semibold">{referrerName}</span>. {t("signup.referral_banner_2")}
                            <div className="text-[11px] text-navy-500 mt-0.5">
                                {t("signup.referral_banner_code")}{" "}
                                <span className="font-mono tracking-wider text-navy-700">
                                    {referralCode}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                <FormSection eyebrow="01" title={t("signup.section_account")}>
                    <div className="grid gap-4">
                        <Field label={t("signup.field_name")} required>
                            <Input
                                required
                                data-testid="signup-name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="h-11 bg-white border-navy-100 focus-visible:ring-gold-500"
                            />
                        </Field>
                        <Field label={t("signup.field_email")} required>
                            <Input
                                type="email"
                                required
                                data-testid="signup-email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="h-11 bg-white border-navy-100 focus-visible:ring-gold-500"
                            />
                        </Field>
                        <Field label={t("signup.field_password")} required hint={t("signup.field_password_hint")}>
                            <Input
                                type="password"
                                required
                                minLength={6}
                                data-testid="signup-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="h-11 bg-white border-navy-100 focus-visible:ring-gold-500"
                            />
                        </Field>
                    </div>
                </FormSection>

                <FormSection eyebrow="02" title={t("signup.section_personal")}>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <Field label={t("signup.field_phone")} required>
                            <Input
                                required
                                type="tel"
                                data-testid="signup-phone"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="+852 5555 0000"
                                className="h-11 bg-white border-navy-100 focus-visible:ring-gold-500"
                            />
                        </Field>
                        <Field label={t("signup.field_gender")}>
                            <Select value={gender} onValueChange={setGender}>
                                <SelectTrigger
                                    data-testid="signup-gender"
                                    className="h-11 bg-white border-navy-100"
                                >
                                    <SelectValue placeholder="Select" />
                                </SelectTrigger>
                                <SelectContent>
                                    {GENDERS.map((g) => (
                                        <SelectItem key={g.value} value={g.value}>
                                            {g.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label={t("signup.field_region")}>
                            <Select value={region} onValueChange={setRegion}>
                                <SelectTrigger
                                    data-testid="signup-region"
                                    className="h-11 bg-white border-navy-100"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {REGIONS.map((r) => (
                                        <SelectItem key={r.value} value={r.value}>
                                            {r.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field
                            label={t("signup.field_dob")}
                            required={membershipType !== "business"}
                        >
                            <Input
                                type="date"
                                data-testid="signup-dob"
                                value={dob}
                                onChange={(e) => setDob(e.target.value)}
                                required={membershipType !== "business"}
                                className="h-11 bg-white border-navy-100 focus-visible:ring-gold-500"
                                max={new Date().toISOString().split("T")[0]}
                            />
                        </Field>
                        <div className="sm:col-span-2">
                            <Field label={t("signup.field_address")} required>
                                <AddressInput
                                    value={addr}
                                    onChange={setAddr}
                                    required
                                    testid="signup-address"
                                />
                            </Field>
                        </div>
                    </div>
                </FormSection>

                <FormSection eyebrow="03" title={t("signup.section_membership")}>
                    <div
                        className="grid gap-3 sm:grid-cols-3"
                        data-testid="signup-membership-type"
                    >
                        {MEMBERSHIP_TYPES.map((mt) => {
                            const Icon = mt.icon;
                            const selected = membershipType === mt.value;
                            return (
                                <button
                                    key={mt.value}
                                    type="button"
                                    data-testid={`signup-mtype-${mt.value}`}
                                    onClick={() => setMembershipType(mt.value)}
                                    className={`text-left rounded-2xl border-2 p-4 transition-all ${
                                        selected
                                            ? "border-gold-500 bg-gold-50/50 shadow-soft"
                                            : "border-navy-100 bg-white hover:border-navy-300"
                                    }`}
                                >
                                    <Icon
                                        className={
                                            selected ? "text-gold-600" : "text-navy-500"
                                        }
                                        size={20}
                                    />
                                    <div
                                        className={`font-semibold mt-2 ${
                                            selected ? "text-navy-900" : "text-navy-700"
                                        }`}
                                    >
                                        {t(`signup.mtype_${mt.value}`)}
                                    </div>
                                    <div className="text-xs text-navy-500 mt-1 leading-snug">
                                        {t(`signup.mtype_${mt.value}_blurb`)}
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {membershipType === "business" && (
                        <div className="mt-4" data-testid="signup-company-block">
                            <Field
                                label={t("signup.field_company")}
                                required
                                hint={t("signup.field_company_hint")}
                            >
                                <Input
                                    required
                                    data-testid="signup-company"
                                    value={companyName}
                                    onChange={(e) => setCompanyName(e.target.value)}
                                    className="h-11 bg-white border-navy-100 focus-visible:ring-gold-500"
                                />
                            </Field>
                        </div>
                    )}
                </FormSection>

                <FormSection
                    eyebrow="04"
                    title={t("signup.section_promo")}
                    subtitle={t("signup.section_promo_hint")}
                >
                    <div className="flex gap-2">
                        <Input
                            data-testid="signup-promo"
                            value={promoCode}
                            onChange={(e) => {
                                setPromoCode(e.target.value.toUpperCase());
                                setPromoStatus({ state: "idle" });
                            }}
                            onBlur={applyPromo}
                            placeholder={t("signup.promo_placeholder")}
                            className="h-11 bg-white border-navy-100 focus-visible:ring-gold-500 uppercase tracking-wide"
                        />
                        <button
                            type="button"
                            data-testid="signup-promo-apply"
                            onClick={applyPromo}
                            className="h-11 px-4 rounded-md border border-navy-700 text-navy-700 text-sm font-semibold hover:bg-navy-50"
                        >
                            {t("common.apply")}
                        </button>
                    </div>
                    {promoStatus.state === "checking" && (
                        <div className="text-xs text-navy-500 mt-1">{t("signup.promo_checking")}</div>
                    )}
                    {promoStatus.state === "valid" && (
                        <div
                            data-testid="signup-promo-valid"
                            className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1 mt-2 inline-flex items-center gap-1.5"
                        >
                            <span>✓</span>
                            <span>
                                <span className="font-semibold">
                                    {promoStatus.promo.code}
                                </span>{" "}
                                {t("signup.promo_applied")} —{" "}
                                {promoStatus.promo.discount_type === "percent"
                                    ? `${promoStatus.promo.discount_value}% off`
                                    : `HK$${promoStatus.promo.discount_value} off`}
                            </span>
                        </div>
                    )}
                    {promoStatus.state === "invalid" && (
                        <div
                            data-testid="signup-promo-invalid"
                            className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-2 py-1 mt-2"
                        >
                            {promoStatus.message}
                        </div>
                    )}

                    <div className="mt-4 pt-4 border-t border-navy-100">
                        <Label className="text-xs uppercase tracking-widest text-navy-500">
                            {t("signup.referral_label")}
                        </Label>
                        <Input
                            data-testid="signup-referral-code"
                            value={referralCode}
                            onChange={(e) =>
                                setReferralCode(e.target.value.toUpperCase().trim())
                            }
                            placeholder={t("signup.referral_placeholder")}
                            className="mt-1.5 h-11 bg-white border-navy-100 focus-visible:ring-gold-500 uppercase tracking-widest font-mono"
                            maxLength={20}
                        />
                        {referrerName && (
                            <div className="text-xs text-emerald-700 mt-1.5">
                                {t("signup.referral_valid", { name: referrerName })}
                            </div>
                        )}
                        {referralCode && !referrerName && (
                            <div className="text-[11px] text-navy-400 mt-1.5">
                                {t("signup.referral_pending")}
                            </div>
                        )}
                    </div>
                </FormSection>

                <FormSection eyebrow="05" title={t("signup.section_consents")}>
                    <div className="rounded-xl bg-cream border border-gold-200/60 p-4 space-y-3">
                        {catalogue.map((c) => (
                            <label
                                key={c.id}
                                htmlFor={`consent-${c.id}`}
                                className="flex gap-3 items-start cursor-pointer"
                                data-testid={`consent-row-${c.id}`}
                            >
                                <Checkbox
                                    id={`consent-${c.id}`}
                                    data-testid={`consent-${c.id}`}
                                    checked={consents[c.id] || false}
                                    onCheckedChange={(v) =>
                                        setConsents((s) => ({
                                            ...s,
                                            [c.id]: Boolean(v),
                                        }))
                                    }
                                    className="mt-0.5 data-[state=checked]:bg-navy-700 data-[state=checked]:border-navy-700"
                                />
                                <span className="text-sm text-navy-700 leading-snug">
                                    <span className="font-medium">
                                        {c.title}
                                        {c.required && (
                                            <span className="text-gold-600 ml-1">*</span>
                                        )}
                                        {LEGAL_DOCS[c.id] && (
                                            <button
                                                type="button"
                                                data-testid={`consent-doc-${c.id}`}
                                                // The row is a <label htmlFor>, so a bare
                                                // click here would also tick the consent
                                                // box the member has not read yet.
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setOpenDoc(c.id);
                                                }}
                                                className="ml-2 text-gold-600 underline underline-offset-2 hover:text-gold-700 font-normal"
                                            >
                                                {t("signup.read_document")}
                                            </button>
                                        )}
                                    </span>
                                    <div className="text-navy-500 text-xs mt-0.5">
                                        {c.body}
                                    </div>
                                </span>
                            </label>
                        ))}
                    </div>
                    <LegalDocDialog
                        doc={LEGAL_DOCS[openDoc]}
                        open={Boolean(openDoc)}
                        onOpenChange={(v) => !v && setOpenDoc(null)}
                        hint={t("signup.legal_doc_hint")}
                    />
                </FormSection>

                {err && (
                    <div
                        data-testid="signup-error"
                        className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                    >
                        {err}
                    </div>
                )}

                <Button
                    type="submit"
                    disabled={busy || missingRequired}
                    data-testid="signup-submit"
                    className="w-full h-12 rounded-full bg-gold-500 hover:bg-gold-600 text-navy-900 font-semibold disabled:opacity-50"
                >
                    {busy ? t("signup.submit_busy") : t("signup.submit")}
                </Button>

                <div className="text-sm text-navy-500 text-center">
                    {t("signup.already_member")}{" "}
                    <Link
                        to="/login"
                        data-testid="signup-login-link"
                        className="text-navy-700 font-semibold hover:text-gold-600"
                    >
                        {t("signup.sign_in")}
                    </Link>
                </div>
            </form>
        </AuthShell>
    );
}

function FormSection({ eyebrow, title, subtitle, children }) {
    return (
        <section className="space-y-3">
            <div className="flex items-baseline gap-3">
                <span className="text-xs font-mono text-gold-600 tracking-widest">
                    {eyebrow}
                </span>
                <h2 className="font-display text-xl text-navy-700 leading-none">
                    {title}
                </h2>
            </div>
            {subtitle && <p className="text-xs text-navy-500 -mt-1">{subtitle}</p>}
            {children}
        </section>
    );
}

function Field({ label, required, hint, children }) {
    return (
        <div>
            <Label className="text-navy-700 text-xs uppercase tracking-widest">
                {label}
                {required && <span className="text-gold-600 ml-1">*</span>}
            </Label>
            <div className="mt-2">{children}</div>
            {hint && <div className="text-[11px] text-navy-500 mt-1">{hint}</div>}
        </div>
    );
}
